//
//  live.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  The D57 live poller behind useLiveFeed: polls a since= list route every 5s
//  while the tab is visible, pauses when hidden, and does one catch-up fetch on
//  resume. Rows merge by notebook_id:seq or id, so re-sent rows never repeat.
//
// The D57 live poller: `useLiveFeed(path, key)` polls a §6e `since=` list route every 5 s while the
// tab is visible, pauses while hidden, and does one catch-up fetch on resume. Items merge by
// `(notebook_id, seq)` when present, else by `id`/`session_id`, so a re-delivered row never duplicates.
// When 2b (SSE) lands, this hook is meant to swap its transport for an `EventSource` behind the same
// reducer -- callers (`Runs.tsx`, `Run.tsx`, `Fleet.tsx`) should not need to change.
//
// `useLiveTimeline` is the same scheduling core aimed at `GET /v1/fleet/runs/{id}`'s embedded
// timeline, whose incremental cursor is `after=<ts>,<notebook_id>,<seq>` (opaque string), not
// `since=<epoch>` -- the run detail route carries no top-level `server_time` (§6e), so its "cursor"
// is just the last row's own `(ts, notebook_id, seq)`.
//
// The visibility-paused scheduling itself lives in `createPoller`, a plain function with no React and
// no DOM dependency (it takes `isVisible`/`onVisibilityChange` as parameters) so it is unit-testable
// under vitest's `node` environment, where `document` does not exist. The two hooks below are thin
// React wrappers around it, defaulting those two to `document.visibilityState` / `visibilitychange`.
import { useEffect, useState } from 'react'
import { api } from './client'
import { isOk } from './envelope'
import type { LivePage, RunTimelineEntry } from './types'

export const POLL_MS = 5000

type Keyed = { stream_id?: string | null; type?: string; notebook_id?: string; seq?: number; id?: string; session_id?: string }

export function keyOf(item: Keyed): string {
  if (item.notebook_id !== undefined && item.seq !== undefined) return `${item.stream_id ?? "l"}:${item.type ?? "deed"}:${item.notebook_id}:${item.seq}`
  if (item.session_id !== undefined) return item.session_id
  if (item.id !== undefined) return item.id
  return JSON.stringify(item)
}

export function merge<T extends Keyed>(prev: T[], next: T[]): T[] {
  const byKey = new Map(prev.map((it) => [keyOf(it), it]))
  for (const it of next) byKey.set(keyOf(it), it)
  return [...byKey.values()]
}

export type LiveState<T, C> = {
  items: T[]
  cursor: C | null
  error: string | null
}

/** `cursor` is `null` on the very first call (the caller decides what that means: omit `since=`
 * entirely, or start from the top of the timeline); a non-null `next` becomes the next call's cursor. */
export type Fetcher<T, C> = (cursor: C | null) => Promise<{ ok: true; items: T[]; next: C } | { ok: false; message: string }>

export type Poller<T, C> = {
  /** Runs one fetch immediately if visible, then schedules the next on a `POLL_MS` timer. Idempotent. */
  start: () => void
  stop: () => void
}

/**
 * The visibility-aware scheduling core, with no React/DOM dependency: pure function of
 * `fetchPage`/`isVisible`/`onVisibilityChange`/`setState`, all injected. Never fetches while
 * `isVisible()` is false; `onVisibilityChange` firing true triggers one catch-up fetch immediately.
 */
export function createPoller<T extends Keyed, C>(
  fetchPage: Fetcher<T, C>,
  setState: (fn: (s: LiveState<T, C>) => LiveState<T, C>) => void,
  isVisible: () => boolean,
  onVisibilityChange: (cb: () => void) => () => void,
  schedule: (fn: () => void, ms: number) => unknown,
  cancelSchedule: (handle: unknown) => void,
): Poller<T, C> {
  let cancelled = false
  let timer: unknown = null
  let itemsRef: T[] = []
  let cursorRef: C | null = null
  let offVisibility: (() => void) | null = null

  const tick = async () => {
    if (cancelled || !isVisible()) return
    const r = await fetchPage(cursorRef)
    if (cancelled) return
    if (!r.ok) {
      setState((s) => ({ ...s, error: r.message }))
      return
    }
    itemsRef = merge(itemsRef, r.items)
    cursorRef = r.next
    setState(() => ({ items: itemsRef, cursor: cursorRef, error: null }))
  }

  const loop = () => {
    timer = schedule(async () => {
      await tick()
      if (!cancelled) loop()
    }, POLL_MS)
  }

  return {
    start() {
      cancelled = false
      tick()
      loop()
      offVisibility = onVisibilityChange(() => {
        if (isVisible()) tick() // catch-up fetch on resume
      })
    },
    stop() {
      cancelled = true
      if (timer !== null) cancelSchedule(timer)
      offVisibility?.()
      offVisibility = null
    },
  }
}

const domVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden'
const domOnVisibilityChange = (cb: () => void) => {
  if (typeof document === 'undefined') return () => {}
  document.addEventListener('visibilitychange', cb)
  return () => document.removeEventListener('visibilitychange', cb)
}
const domSchedule = (fn: () => void, ms: number) => setTimeout(fn, ms)
const domCancel = (h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>)

/**
 * Polls `${basePath}?since=<epoch>` (plus `extraQuery`) every `POLL_MS` while the tab is visible.
 * Pauses entirely when `document.visibilityState === 'hidden'`; fires one catch-up fetch on resume.
 * `enabled=false` disables polling without unmounting (e.g. a closed/backgrounded detail pane).
 */
export function useLiveFeed<T extends Keyed>(basePath: string | null, token: string | null, extraQuery = '', enabled = true): LiveState<T, number> & { serverTime: number | null } {
  const [state, setState] = useState<LiveState<T, number>>({ items: [], cursor: null, error: null })

  useEffect(() => {
    setState({ items: [], cursor: null, error: null })
  }, [basePath, extraQuery])

  useEffect(() => {
    if (!basePath || !enabled || !token) return
    const fetchPage: Fetcher<T, number> = async (since) => {
      const sep = extraQuery ? '&' : ''
      const sinceQ = since === null ? '' : `since=${since}`
      const q = [extraQuery, sinceQ].filter(Boolean).join(sep) || sinceQ
      const r = await api<LivePage<T>>(`${basePath}${q ? `?${q}` : ''}`, {}, token)
      return isOk(r) ? { ok: true, items: r.data.items, next: r.data.server_time } : { ok: false, message: r.error.message }
    }
    const poller = createPoller<T, number>(fetchPage, setState, domVisible, domOnVisibilityChange, domSchedule, domCancel)
    poller.start()
    return () => poller.stop()
  }, [basePath, extraQuery, enabled, token])

  return { ...state, serverTime: state.cursor }
}

// `(ts, seq)` alone is not unique across notebooks in a run -- two notebooks can log the same
// `(ts, seq)` pair independently, `seq` is only unique within one notebook -- so `notebook_id` is
// part of the sort and the cursor, not just a display tie-break (§6e, run-detail timeline).
type AfterCursor = { ts: number; notebookId: string; seq: number }

/** Exported for unit testing without mounting the hook: builds `after=<ts>,<notebook_id>,<seq>`, or
 * `''` for the first page (`cursor === null`). */
export function afterQuery(after: AfterCursor | null, types: string[] = []): string {
  const q = [after ? `after=${after.ts},${after.notebookId},${after.seq}` : '', types.length ? `types=${types.join(',')}` : ''].filter(Boolean).join('&')
  return q ? `?${q}` : ''
}

/** Exported for unit testing: the newest row of a `ts desc, notebook_id desc, seq desc` page becomes
 * the next `after=` cursor, so the next poll asks for rows strictly newer than that. */
export function nextAfterCursor(items: RunTimelineEntry[], previous: AfterCursor | null): AfterCursor {
  const newest = items[0]
  return newest ? { ts: newest.ts, notebookId: newest.notebook_id, seq: newest.seq } : (previous ?? { ts: 0, notebookId: '', seq: 0 })
}

/**
 * Polls `GET /v1/fleet/runs/{sessionId}?after=<ts>,<notebook_id>,<seq>` for the run detail timeline
 * (§6e). Same visibility pause/catch-up as `useLiveFeed`; `run` (the header/totals) is refreshed
 * alongside the timeline on every tick since the two share one response, and only replaced when a
 * fetch succeeds.
 */
export function useLiveTimeline(sessionId: string | null, token: string | null, onRun: (run: unknown) => void, enabled = true, types: string[] = []): LiveState<RunTimelineEntry, AfterCursor> {
  const typesKey = types.join(',')
  const [state, setState] = useState<LiveState<RunTimelineEntry, AfterCursor>>({ items: [], cursor: null, error: null })

  useEffect(() => {
    setState({ items: [], cursor: null, error: null })
  }, [sessionId, typesKey])

  useEffect(() => {
    if (!sessionId || !enabled || !token) return
    const fetchPage: Fetcher<RunTimelineEntry, AfterCursor> = async (after) => {
      const r = await api<{ timeline: { items: RunTimelineEntry[]; next_cursor: string | null } } & Record<string, unknown>>(`/fleet/runs/${sessionId}${afterQuery(after, typesKey ? typesKey.split(',') : [])}`, {}, token)
      if (!isOk(r)) return { ok: false, message: r.error.message }
      onRun(r.data)
      const items = r.data.timeline.items
      return { ok: true, items, next: nextAfterCursor(items, after) }
    }
    const poller = createPoller<RunTimelineEntry, AfterCursor>(fetchPage, setState, domVisible, domOnVisibilityChange, domSchedule, domCancel)
    poller.start()
    return () => poller.stop()
  }, [sessionId, enabled, token, onRun, typesKey])

  return state
}

/** `server_time - last_at`, for the sync-lag readout next to a live dot. */
export function lagSeconds(serverTime: number | null, lastAt: number | null | undefined): number | null {
  if (serverTime === null || lastAt === null || lastAt === undefined) return null
  return Math.max(0, serverTime - lastAt)
}
